import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts"

window.echarts = echarts

const TOOLTIP_STYLE = {
  confine: true,
  textStyle: { align: "left", color: "#444", fontSize: 13 },
  backgroundColor: "#FFFFFF",
  borderColor: "rgba(0, 0, 0, 0.1)",
  borderWidth: 1,
  extraCssText: "box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.05);",
  trigger: "axis"
}

const AXIS_LABEL_STYLE = { fontSize: 12, color: "#767676" }

const COLLECTION_COLORS = [
  "#00A00D", "#1f77b4", "#d62728", "#ff7f0e", "#9467bd",
  "#8c564b", "#e377c2", "#17becf", "#bcbd22", "#7f7f7f",
  "#003f5c", "#58508d", "#bc5090", "#ff6361", "#ffa600",
  "#6a3d9a", "#2ca02c", "#33a02c", "#e31a1c", "#1b9e77",
  "#d95f02", "#7570b3", "#e7298a", "#66a61e", "#e6ab02",
  "#a6761d", "#0f4c5c", "#5f0f40", "#9a031e", "#e36414",
  "#0b6e4f", "#3a86ff", "#8338ec", "#ff006e", "#fb5607",
  "#2d6a4f", "#d00000", "#03045e", "#00b4d8", "#4361ee"
]

export default class extends Controller {
  static targets = [
    "globalChart", "allocationChart", "quantityChart",
    "biomeSelect", "levelSelect", "collectionCheck", "collectionPill",
    "dataJson", "loading"
  ]

  static values = {
    initSlug: String,
    initId: Number,
    collections: Array,
    biome: String,
    level: String,
    result: Object
  }

  connect() {
    this.globalChartInstance = null
    this.allocationChartInstance = null
    this.quantityChartInstance = null
    this.highlightedCollection = null

    this.handleResize = this.handleResize.bind(this)
    window.addEventListener("resize", this.handleResize)

    this.updateAllPills()
    this.renderAllCharts()
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    this.disposeCharts()
  }

  handleResize() {
    [this.globalChartInstance, this.allocationChartInstance, this.quantityChartInstance].forEach(chart => {
      if (chart) chart.resize()
    })
  }

  disposeCharts() {
    [this.globalChartInstance, this.allocationChartInstance, this.quantityChartInstance].forEach(chart => {
      if (chart) chart.dispose()
    })
  }

  selectedCollections() {
    return this.collectionCheckTargets
      .filter(input => input.checked)
      .map(input => input.value)
  }

  toggleCollection(event) {
    this.updatePill(event.currentTarget.closest("label"))
    this.loadData()
  }

  selectAll() {
    this.collectionCheckTargets.forEach(input => { input.checked = true })
    this.updateAllPills()
    this.loadData()
  }

  clearAll() {
    this.collectionCheckTargets.forEach(input => { input.checked = false })
    this.updateAllPills()
    this.loadData()
  }

  updateAllPills() {
    this.collectionPillTargets.forEach(pill => this.updatePill(pill))
  }

  updatePill(pill) {
    const checkbox = pill.querySelector("input[type=checkbox]")
    const color = pill.dataset.color || COLLECTION_COLORS[0]
    const dot = pill.querySelector(".pill-dot")

    if (checkbox.checked) {
      pill.style.backgroundColor = color
      pill.style.color = this.textColorFor(color)
      pill.style.borderColor = "rgba(0, 0, 0, 0.1)"
      dot.style.backgroundColor = "rgba(255, 255, 255, 0.9)"
    } else {
      pill.style.backgroundColor = "#f0f0f0"
      pill.style.color = "#9ca3af"
      pill.style.borderColor = "transparent"
      dot.style.backgroundColor = "#c0c0c0"
    }
  }

  textColorFor(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255
    const g = parseInt(hex.slice(3, 5), 16) / 255
    const b = parseInt(hex.slice(5, 7), 16) / 255
    const linearize = v => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
    const luminance = 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b)
    return luminance > 0.4 ? "#111827" : "#FFFFFF"
  }

  changeFilter() {
    this.search()
  }

  search() {
    this.biomeValue = this.biomeSelectTarget.value
    this.levelValue = this.levelSelectTarget.value
    this.loadData()
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
  }

  async loadData() {
    this.showLoading()
    const params = new URLSearchParams({
      territory: this.biomeValue,
      level: this.levelValue,
      initiative_id: this.initIdValue
    })
    this.selectedCollections().forEach(collection => params.append("collections[]", collection))

    try {
      const resp = await fetch(`/api/accuracies/compare?${params}`)
      const data = await resp.json()
      this.resultValue = data
      this.dataJsonTarget.textContent = JSON.stringify(data)
      this.renderAllCharts()
    } catch (e) {
      console.error("Error loading comparison data:", e)
    } finally {
      this.hideLoading()
    }
  }

  collectionName(name) {
    const found = this.collectionsValue.find(c => c.name === name)
    return found?.display_name || name
  }

  collectionColor(name) {
    const found = this.collectionsValue.find(c => c.name === name)
    return found?.color || COLLECTION_COLORS[0]
  }

  highlightCollection(event) {
    const name = event.currentTarget.dataset.collection
    if (!name || name === this.highlightedCollection) return
    this.highlightedCollection = name
    this.setPillHighlight(event.currentTarget)
    this.renderAllCharts()
  }

  unhighlightCollection() {
    if (!this.highlightedCollection) return
    this.highlightedCollection = null
    this.clearPillHighlight()
    this.renderAllCharts()
  }

  setPillHighlight(pill) {
    this.collectionPillTargets.forEach(p => p.classList.remove("pill-highlighted"))
    pill.classList.add("pill-highlighted")
  }

  clearPillHighlight() {
    this.collectionPillTargets.forEach(p => p.classList.remove("pill-highlighted"))
  }

  renderAllCharts() {
    const data = this.resultValue || {}

    this.renderLineChart(this.globalChartTarget, "globalChartInstance", data, "overall_accuracy", null, "decimal", true)
    const errorBounds = this.sharedErrorBounds(data)
    this.renderLineChart(this.allocationChartTarget, "allocationChartInstance", data, "allocation_error", errorBounds, "integer", false)
    this.renderLineChart(this.quantityChartTarget, "quantityChartInstance", data, "quantity_error", errorBounds, "integer", false)
  }

  sharedErrorBounds(data) {
    const values = ["allocation_error", "quantity_error"].flatMap(key => {
      return Object.keys(data).flatMap(name => (data[name]?.[key] || []).map(a => a.value))
    }).filter(v => v != null)

    return this.computeYBounds(values, "integer")
  }

  computeYBounds(allValues, rounding = "decimal") {
    if (!allValues || allValues.length === 0) return {}
    const rawMin = Math.min(...allValues)
    const rawMax = Math.max(...allValues)
    const padding = (rawMax - rawMin) * 0.15 || 1
    const round = rounding === "integer" ? Math.round : v => Math.round(v * 10) / 10
    const min = Math.max(0, round(rawMin - padding))
    const max = Math.min(100, round(rawMax + padding))
    return { min, max }
  }

  renderLineChart(target, instanceKey, data, key, yBounds, rounding = "decimal", percent = false) {
    if (!target) return

    if (!this[instanceKey]) {
      this[instanceKey] = echarts.init(target)
    }

    const names = Object.keys(data)
    const years = [...new Set(names.flatMap(name => (data[name]?.[key] || []).map(a => a.year)))].sort((a, b) => a - b)

    if (years.length === 0 || names.length === 0) {
      this[instanceKey].clear()
      return
    }

    const highlighted = this.highlightedCollection
    const series = names.map((name) => {
      const byYear = new Map((data[name]?.[key] || []).map(a => [a.year, a.value]))
      const color = this.collectionColor(name)
      const isHighlighted = highlighted === name
      return {
        name: this.collectionName(name),
        type: "line",
        showSymbol: false,
        data: years.map(year => byYear.get(year) ?? null),
        itemStyle: {
          color,
          opacity: highlighted && !isHighlighted ? 0.01 : 1
        },
        lineStyle: {
          color,
          width: highlighted ? (isHighlighted ? 3 : 1) : 2
        }
      }
    })

    const yAxis = yBounds || this.computeYBounds(
      names.flatMap(name => (data[name]?.[key] || []).map(a => a.value)).filter(v => v != null),
      rounding
    )

    this[instanceKey].setOption({
      tooltip: { ...TOOLTIP_STYLE, trigger: "axis" },
      grid: { top: "8%", right: "20px", bottom: "8%", left: "20px", containLabel: true },
      xAxis: {
        type: "category",
        data: years,
        boundaryGap: false,
        axisPointer: { type: "shadow" },
        axisLabel: { ...AXIS_LABEL_STYLE, padding: [8, 0, 0, 0] },
        axisLine: { color: "#DDD" }
      },
      yAxis: {
        type: "value",
        ...yAxis,
        axisTick: { show: false },
        axisLine: { color: "#DDD" },
        splitLine: { lineStyle: { color: "rgba(0, 0, 0, 0.05)" } },
        axisLabel: { ...AXIS_LABEL_STYLE, formatter: percent ? v => `${v}%` : undefined }
      },
      series
    }, { notMerge: true })
  }
}
